import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

// Howyin palette
const INK = "#1A1614";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const HAIR = "#E8E1D3";
const MUTED = "#8A857F";

const badge = (ok) => (
  <span
    style={{
      background: ok ? "#E7F6EC" : "#FDEAEA",
      color: ok ? "#16A34A" : "#DC2626",
      fontSize: 11,
      fontWeight: 700,
      padding: "2px 9px",
      borderRadius: 10,
    }}
  >
    {ok ? "Signed in" : "Failed"}
  </span>
);

const Stat = ({ label, value }) => (
  <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, padding: "10px 16px", minWidth: 150 }}>
    <div style={{ fontSize: 11, color: MUTED }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>{value}</div>
  </div>
);

// Who signed in to the admin panel, when, from where, with what browser.
const AdminLogins = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ successful: 0, failed: 0, distinctIps: 0 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const q =
        `admin/loginLogs?page=${page}&limit=${limit}` +
        (search ? `&search=${encodeURIComponent(search)}` : "") +
        (result ? `&result=${result}` : "");
      const res = await apiInstanceFetch.get(q);
      if (res && res.status) {
        setLogs(res.logs || []);
        setTotal(res.total || 0);
        setSummary(res.summary30d || { successful: 0, failed: 0, distinctIps: 0 });
      }
    } catch (e) {
      console.error("admin loginLogs fetch", e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, result]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const th = { textAlign: "left", padding: "10px 12px", fontSize: 12, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${HAIR}`, whiteSpace: "nowrap" };
  const td = { padding: "10px 12px", fontSize: 13, color: INK, borderBottom: `1px solid ${HAIR}`, verticalAlign: "top" };

  return (
    <div style={{ padding: 16 }}>
      <Title name="Admin Logins" />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" }}>
        <Stat label="Successful sign-ins, 30 days" value={summary.successful} />
        <Stat label="Failed attempts, 30 days" value={summary.failed} />
        <Stat label="Different IP addresses, 30 days" value={summary.distinctIps} />
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "12px 0" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (page !== 1) setPage(1);
              else fetchLogs();
            }
          }}
          placeholder="Search email / name / IP / country / browser…"
          style={{ flex: "1 1 280px", padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM }}
        />
        <select
          value={result}
          onChange={(e) => {
            setResult(e.target.value);
            setPage(1);
          }}
          style={{ padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM }}
        >
          <option value="">All attempts</option>
          <option value="success">Signed in</option>
          <option value="failed">Failed</option>
        </select>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(parseInt(e.target.value, 10) || 25);
            setPage(1);
          }}
          style={{ padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM }}
        >
          {[25, 50, 100, 200].map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
        <button
          onClick={() => {
            if (page !== 1) setPage(1);
            else fetchLogs();
          }}
          style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: GOLD, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
        >
          Search
        </button>
        <span style={{ fontSize: 12, color: MUTED }}>{total} records</span>
      </div>

      <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
          <thead>
            <tr>
              <th style={th}>Date / Time</th>
              <th style={th}>Admin</th>
              <th style={th}>Result</th>
              <th style={th}>IP address</th>
              <th style={th}>Location</th>
              <th style={th}>Browser</th>
              <th style={th}>System</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={7}>
                  Loading…
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={7}>
                  No sign-in records yet. Recording started when this page was added.
                </td>
              </tr>
            ) : (
              logs.map((l) => (
                <tr key={l._id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{dayjs(l.createdAt).format("DD MMM YYYY")}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{dayjs(l.createdAt).format("hh:mm:ss A")}</div>
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{l.name || (l.adminId ? "Admin" : "Unknown account")}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{l.email || "—"}</div>
                  </td>
                  <td style={td}>
                    {badge(l.success)}
                    {l.method === "mfa" ? <span style={{ fontSize: 10, color: MUTED, marginLeft: 6 }}>2-step code</span> : null}
                    {!l.success && l.reason ? <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>{l.reason}</div> : null}
                  </td>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{l.ip || "—"}</td>
                  <td style={td}>
                    <div>{[l.city, l.country].filter(Boolean).join(", ") || "—"}</div>
                    {l.isp ? <div style={{ fontSize: 11, color: MUTED }}>{l.isp}</div> : null}
                  </td>
                  <td style={td}>
                    <div>{l.browser || "—"}</div>
                    {l.userAgent ? (
                      <div title={l.userAgent} style={{ fontSize: 10, color: "#B6AF9F", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {l.userAgent}
                      </div>
                    ) : null}
                  </td>
                  <td style={td}>
                    <div>{l.os || "—"}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{l.device || ""}</div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 14 }}>
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${HAIR}`, background: page <= 1 ? "#F3F0E9" : "#fff", color: INK, cursor: page <= 1 ? "default" : "pointer", fontSize: 13 }}
        >
          Prev
        </button>
        <span style={{ fontSize: 13, color: MUTED }}>
          Page {page} / {totalPages}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${HAIR}`, background: page >= totalPages ? "#F3F0E9" : "#fff", color: INK, cursor: page >= totalPages ? "default" : "pointer", fontSize: 13 }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminLogins;
